import {
  LegalLayout,
  Heading,
  Text,
  UnorderedList,
  ListItem,
  Link,
  RouterLink,
} from '../components/LegalLayout';

export default function PrivacyPolicy() {
  return (
    <LegalLayout title="Privacy Policy" lastUpdated="June 7, 2026">
      <Text>
        This Privacy Policy (&ldquo;Policy&rdquo;) describes how IvoryShoulder,
        LLC, doing business as WagerWatchdog (&ldquo;WagerWatchdog,&rdquo;
        &ldquo;we,&rdquo; &ldquo;us,&rdquo; or &ldquo;our&rdquo;), collects,
        uses, and discloses information about you when you access or use our
        website at wagerwatchdog.com, our application at app.wagerwatchdog.com,
        and any related services (collectively, the &ldquo;Services&rdquo;).
      </Text>
      <Text>
        By accessing or using the Services, you acknowledge that you have read,
        understood, and agree to be bound by this Policy. If you do not agree,
        please do not access or use the Services.
      </Text>

      <Heading as="h2">1. Information We Collect</Heading>

      <Heading as="h3">1.1 Information You Provide</Heading>
      <Text>We collect information you voluntarily provide, including:</Text>
      <UnorderedList>
        <ListItem>
          <strong>Account Information:</strong> Name, email address, and
          password when you create an account.
        </ListItem>
        <ListItem>
          <strong>Payment Information:</strong> Billing details processed through
          our third-party payment processor, Stripe. We do not store your full
          credit card numbers on our servers.
        </ListItem>
        <ListItem>
          <strong>Communications:</strong> Information you provide when
          contacting support or providing feedback.
        </ListItem>
        <ListItem>
          <strong>Waitlist Information:</strong> Name and email address submitted
          through our waitlist form.
        </ListItem>
      </UnorderedList>

      <Heading as="h3">1.2 Information Collected Automatically</Heading>
      <Text>
        When you use the Services, we automatically collect certain information,
        including:
      </Text>
      <UnorderedList>
        <ListItem>
          <strong>Log Data:</strong> IP address, browser type and version,
          operating system, referring URLs, pages viewed, access times, and other
          standard server log information.
        </ListItem>
        <ListItem>
          <strong>Device Information:</strong> Device type, screen resolution,
          and unique device identifiers.
        </ListItem>
        <ListItem>
          <strong>Usage Data:</strong> Features accessed, actions taken within
          the Services, and interaction patterns.
        </ListItem>
        <ListItem>
          <strong>Cookies and Similar Technologies:</strong> We use cookies and
          similar tracking technologies to maintain session state, remember
          preferences, and understand usage patterns. You can control cookie
          settings through your browser.
        </ListItem>
      </UnorderedList>

      <Heading as="h2">2. How We Use Your Information</Heading>
      <Text>
        We use the information we collect for the following limited purposes:
      </Text>
      <UnorderedList>
        <ListItem>
          <strong>Providing the Services:</strong> To operate, maintain, and
          improve our platform, including account management, authentication, and
          delivering analytics.
        </ListItem>
        <ListItem>
          <strong>Security and Fraud Prevention:</strong> To protect the
          Services, detect and prevent fraud, abuse, and unauthorized access, and
          to enforce our Terms of Service.
        </ListItem>
        <ListItem>
          <strong>Understanding General Trends:</strong> To analyze aggregate,
          de-identified usage data to understand how the Services are used and to
          improve user experience and product development.
        </ListItem>
        <ListItem>
          <strong>Regulatory Compliance:</strong> To comply with applicable laws,
          regulations, legal processes, and governmental requests.
        </ListItem>
        <ListItem>
          <strong>Communications:</strong> To send transactional emails (e.g.,
          account confirmations, security alerts, billing notices) and, with your
          consent, product updates.
        </ListItem>
      </UnorderedList>

      <Heading as="h2">3. We Do Not Sell Your Personal Data</Heading>
      <Text>
        <strong>
          WagerWatchdog does not sell, rent, lease, or trade your personal
          information to third parties for their marketing or commercial
          purposes.
        </strong>{' '}
        We have never sold personal data and have no plans to do so. This
        commitment applies to all categories of personal information we collect.
      </Text>

      <Heading as="h2">4. How We Share Your Information</Heading>
      <Text>
        We share information only in the following limited circumstances:
      </Text>
      <UnorderedList>
        <ListItem>
          <strong>Service Providers (Subprocessors):</strong> We share
          information with trusted third-party service providers who assist us in
          operating the Services, subject to contractual obligations to protect
          your data. See our{' '}
          <Link as={RouterLink} to="/subprocessors">
            Subprocessors page
          </Link>{' '}
          for a current list.
        </ListItem>
        <ListItem>
          <strong>Legal Requirements:</strong> We may disclose information if
          required by law, regulation, subpoena, court order, or other
          governmental request, or when we believe in good faith that disclosure
          is necessary to protect our rights, your safety, or the safety of
          others.
        </ListItem>
        <ListItem>
          <strong>Business Transfers:</strong> In connection with a merger,
          acquisition, reorganization, bankruptcy, or sale of assets, your
          information may be transferred as a business asset. We will notify you
          of any such change.
        </ListItem>
        <ListItem>
          <strong>With Your Consent:</strong> We may share information with your
          explicit consent for purposes not described in this Policy.
        </ListItem>
      </UnorderedList>

      <Heading as="h2">5. Data Retention</Heading>
      <Text>
        We retain your personal information for as long as your account is active
        or as needed to provide the Services. We will also retain information as
        necessary to comply with legal obligations, resolve disputes, and
        enforce agreements. When your data is no longer needed, we will securely
        delete or anonymize it.
      </Text>

      <Heading as="h2">6. Data Security</Heading>
      <Text>
        We implement industry-standard technical and organizational measures to
        protect your information, including encryption in transit (TLS/SSL),
        secure authentication, access controls, and regular security
        assessments. However, no method of transmission or storage is 100%
        secure, and we cannot guarantee absolute security.
      </Text>

      <Heading as="h2">7. Your Rights and Choices</Heading>
      <Text>
        Depending on your jurisdiction, you may have certain rights regarding
        your personal information:
      </Text>
      <UnorderedList>
        <ListItem>
          <strong>Access and Portability:</strong> Request a copy of the personal
          information we hold about you.
        </ListItem>
        <ListItem>
          <strong>Correction:</strong> Request correction of inaccurate personal
          information.
        </ListItem>
        <ListItem>
          <strong>Deletion:</strong> Request deletion of your personal
          information, subject to legal retention requirements.
        </ListItem>
        <ListItem>
          <strong>Opt-Out:</strong> Opt out of marketing communications at any
          time by using the unsubscribe link in emails or contacting us.
        </ListItem>
        <ListItem>
          <strong>Cookie Preferences:</strong> Control cookies through your
          browser settings.
        </ListItem>
      </UnorderedList>
      <Text>
        To exercise any of these rights, contact us at{' '}
        <Link href="mailto:privacy@wagerwatchdog.com">
          privacy@wagerwatchdog.com
        </Link>
        . We will respond within 30 days (or shorter if required by applicable
        law).
      </Text>

      <Heading as="h2">8. International Data Transfers</Heading>
      <Text>
        Your information may be processed and stored in the United States or
        other countries where our service providers operate. By using the
        Services, you consent to the transfer of your information to countries
        that may have different data protection laws than your country of
        residence.
      </Text>

      <Heading as="h2">9. Children&rsquo;s Privacy</Heading>
      <Text>
        The Services are not intended for individuals under the age of 18. We do
        not knowingly collect personal information from children. If you believe
        we have collected information from a child, please contact us at{' '}
        <Link href="mailto:privacy@wagerwatchdog.com">
          privacy@wagerwatchdog.com
        </Link>{' '}
        and we will promptly delete it.
      </Text>

      <Heading as="h2">10. Third-Party Links</Heading>
      <Text>
        The Services may contain links to third-party websites or services. We
        are not responsible for the privacy practices of those third parties. We
        encourage you to review the privacy policies of any third-party sites
        you visit.
      </Text>

      <Heading as="h2">11. Changes to This Policy</Heading>
      <Text>
        We may update this Policy from time to time. We will notify you of
        material changes by posting the updated Policy on this page with a
        revised &ldquo;Last updated&rdquo; date. For significant changes, we may
        also provide additional notice via email or an in-app notification.
        Continued use of the Services after changes constitutes acceptance of the
        updated Policy.
      </Text>

      <Heading as="h2">12. Contact Us</Heading>
      <Text>
        If you have questions or concerns about this Privacy Policy or our data
        practices, contact us at:
      </Text>
      <Text>
        IvoryShoulder, LLC DBA WagerWatchdog
        <br />
        Email:{' '}
        <Link href="mailto:privacy@wagerwatchdog.com">
          privacy@wagerwatchdog.com
        </Link>
      </Text>
    </LegalLayout>
  );
}
